import { Button } from 'primereact/button';
import { useEffect, useState, useRef } from 'react';
import { userOption, userResult } from '../../models/orders/order-list-model';
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar, CalendarChangeEvent } from 'primereact/calendar';
import orderServices from '../../services/orders/orderservice';
import { RadioButton } from "primereact/radiobutton";

interface SearchPanelProps {
    onSearch: (filters: {
        startDate?: Date;
        endDate?: Date;
        orderID?: number;
        invoiceNo?: number;
        userID?: number;
        orderType?: number;
        infoMessage?: string;
    }) => void;
    onReset: () => any;
    setLoading: (loading: boolean) => void;
}

const OrderListSearch: React.FC<SearchPanelProps> = ({ onSearch, setLoading, onReset }) => {
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [selectedUser, setSelectedUser] = useState<any>();
    const blankUserDetail: userOption[] = [{ userID: 0, name: '', primaryContactNbr: '' }];
    const [orderUsers, setOrderUsers] = useState<userOption[]>(blankUserDetail);
    const [orderUser, setOrderUser] = useState<userResult[]>([]);
    const [selectedOrderType, setSelectedOrderType] = useState<number | null>(1);
    const [orderNo, setOrderNo] = useState<any>();
    const [invoiceNo, setInvoiceNo] = useState<any>();
    const [resetKey, setResetKey] = useState(0);
    const [dropdownValue, setDropdownValue] = useState<any>();

    var orderTypeInfo: Record<number, string> = {
        1: "All orders (filtered by order created date)",
        2: "Not yet invoiced (filtered by order date)",
        3: "Partially and fully invoiced (filtered by invoice date)",
        4: "Only partially invoiced (filtered by invoice date)",
        5: "Not yet invoiced order (filtered by invoice date)"
    };

    const handleDropdownChange = (e: DropdownChangeEvent) => {
    const selectedUserId = e.value;
    const selectedUser = orderUser.find(user => user.userID === selectedUserId);
 
    if (selectedUser) {
      const [name, primaryContactNbr] = selectedUser.detail.split(",").map((item: string) => item.trim());
      setSelectedUser({ name, primaryContactNbr });
      setDropdownValue(selectedUserId);
    }
  };
 
    const handleDropdownFilter = (e: DropdownFilterEvent) => {
        if (e.filter.length > 2) {
            orderServices.getOrderUsers(e.filter).then((response: userOption[]) => {
                if (response.length > 0) {
                    setOrderUsers(response);
                }
            });
        }
    };

    useEffect(() => {
        setOrderUser(orderUsers.map((user) => ({
            userID: user.userID,
            detail: user.userID !== 0 ? `${user.name}, ${user.primaryContactNbr}` : "",
        })));
    }, [orderUsers]);

    const handleSearch = () => {
        setLoading(true);
        const filters: {
            startDate?: Date;
            endDate?: Date;
            orderID?: number;
            invoiceNo?: number;
            userID?: number;
            orderType?: number;
            resetPagination?: boolean;
            infoMessage?: string;
        } = {
            resetPagination: true,
            infoMessage: orderTypeInfo[selectedOrderType || 1]
        };

        if (startDate) filters.startDate = startDate;
        if (endDate) filters.endDate = endDate;

        if (orderNo) {
            const orderId = parseInt(orderNo);
            if (!isNaN(orderId)) filters.orderID = orderId;
        }

        if (invoiceNo) {
            const invoiceNum = parseInt(invoiceNo);
            if (!isNaN(invoiceNum)) filters.invoiceNo = invoiceNum;
        }

        if (selectedUser) filters.userID = dropdownValue;
        if (selectedOrderType) filters.orderType = selectedOrderType;

        if (Object.keys(filters).length > 0) {
            onSearch(filters);
        } else {
            setLoading(false);
        }
    };

    const handleStartDateChange = (e: CalendarChangeEvent) => {
        setStartDate(e.value instanceof Date ? e.value : null);
    };

    const handleEndDateChange = (e: CalendarChangeEvent) => {
        setEndDate(e.value instanceof Date ? e.value : null);
    };

    const handleReset = () => {
        onReset();
        setOrderUsers(blankUserDetail);
        setSelectedUser(null);
        setStartDate(null);
        setEndDate(null);
        setSelectedOrderType(1); 
        setOrderNo("");
        setInvoiceNo("");
        setResetKey(prev => prev + 1);
    };

    return (
        <div className="card p-3 w-full">
            <div className="flex flex-wrap align-items-center gap-5 mb-4">
                {[
                    { id: 1, label: 'Orders' },
                    { id: 2, label: 'Open Orders' },
                    { id: 3, label: 'Invoiced' },
                    { id: 4, label: 'Partial Invoiced' },
                    { id: 5, label: 'Partial Open Orders' }
                ].map(order => (
                    <div className="flex align-items-center" key={order.id}>
                        <RadioButton
                            inputId={`order-type-${order.id}`}
                            name="orderType"
                            value={order.id}
                            onChange={(e) => setSelectedOrderType(e.value)}
                            checked={selectedOrderType === order.id}
                            className="mr-2"
                        />
                        <label htmlFor={`order-type-${order.id}`} className="ml-2">{order.label}</label>
                    </div>
                ))}
            </div>

            <div className="grid">
                <div className="col-12 md:col-6 lg:col-3 mb-3">
                    <Dropdown
                        id="SearchUser"
                        name="SearchUser"
                        options={orderUser || []}
                        optionLabel="detail"
                        optionValue="userID"
                        value={dropdownValue}
                        onChange={handleDropdownChange}
                        onFilter={handleDropdownFilter}
                        filter
                        placeholder="Search for User/Mobile"
                        className="w-full"
                    />
                </div>

                <div className="col-12 md:col-6 lg:col-3 mb-3">
                    <span className="p-float-label">
                        <InputText
                            id="OrderNo"
                            name="OrderNo"
                            value={orderNo}
                            type="number"
                            onChange={(e) => setOrderNo(e.target.value)}
                            className="w-full"
                        />
                        <label htmlFor="OrderNo">Order No#</label>
                    </span>
                </div>

                <div className="col-12 md:col-6 lg:col-3 mb-3">
                    <span className="p-float-label">
                        <InputText
                            id="InvoiceNo"
                            name="InvoiceNo"
                            type="number"
                            value={invoiceNo}
                            onChange={(e) => setInvoiceNo(e.target.value)}
                            className="w-full"
                        />
                        <label htmlFor="InvoiceNo">Invoice No#</label>
                    </span>
                </div>

                <div className="col-12 md:col-6 lg:col-3 mb-3">
                    <div className="flex gap-3">
                        <Calendar
                            key={`start-${resetKey}`}
                            value={startDate}
                            onChange={handleStartDateChange}                           
                            dateFormat="dd/mm/yy"
                            className="flex-1"
                            placeholder="Start date"
                            readOnlyInput
                        />
                        <Calendar
                            key={`end-${resetKey}`}
                            value={endDate}
                            onChange={handleEndDateChange}   
                            dateFormat="dd/mm/yy"                       
                            className="flex-1"
                            placeholder="End date"
                            readOnlyInput
                        />
                    </div>
                </div>

                <div className="col-12 md:col-6 lg:col-3"></div>
                <div className="col-12 md:col-6 lg:col-3"></div>

                <div className="col-12 md:col-6 lg:col-3">
                    <Button
                        className="w-full"
                        onClick={handleSearch}
                        severity="success"
                        label="Filter"
                    />
                </div>
                <div className="col-12 md:col-6 lg:col-3">
                    <Button
                        className="w-full"
                        onClick={handleReset}
                        severity="secondary"
                        text
                        raised
                        label="Reset"
                    />
                </div>
            </div>
        </div>
    );
};

export default OrderListSearch;